import api from './api'
import { BASE } from './config'
import { DEFAULT_PAGINATION_LIMIT } from '@/constants/common'
import { CHAT_API_TYPE_DIRECT, CHAT_API_TYPE_GROUP } from '@/constants/chats'
import { haUserRoles, haUserRoleIds, saUserRoleIds } from '@/constants/users'
import { datesRange, dateFormatter } from '@/helpers/formatters'

function isGroupChatPayload(contact) {
  return (
    contact.type === CHAT_API_TYPE_GROUP ||
    contact.chat_type === CHAT_API_TYPE_GROUP
  )
}

function buildEpisodeOfCareRange(episode) {
  if (!episode) {
    return ''
  }
  const from = episode.episode_start_date || episode.start_date
  const to = episode.episode_end_date || episode.end_date
  if (from && to) {
    return datesRange({ from, to })
  }
  if (from) {
    return dateFormatter({ date: from })
  }
  if (to) {
    return dateFormatter({ date: to })
  }
  return ''
}

function applyGroupChatPresentation(prepared, { listKind } = {}) {
  const treatAsGroup =
    listKind === CHAT_API_TYPE_GROUP || isGroupChatPayload(prepared)
  if (!treatAsGroup) {
    return prepared
  }
  const eoc = prepared.episode_of_care
  let contactUser = { ...prepared.contactUser }
  const patientName =
    contactUser.patient_full_name ||
    eoc?.patient_full_name ||
    prepared.offer?.patient_full_name ||
    ''
  if (patientName) {
    contactUser = { ...contactUser, patient_full_name: patientName }
  }
  return {
    ...prepared,
    contactUser,
    isGroupChat: true,
    episodeOfCareRange: buildEpisodeOfCareRange(eoc),
  }
}

const CHAT_LABEL_VARIANTS = {
  SA: 'sa',
  HA: 'ha',
  CLINICIAN: 'clinician',
}

class ChatsService {
  getListKindForPresentation(contact) {
    return isGroupChatPayload(contact)
      ? CHAT_API_TYPE_GROUP
      : CHAT_API_TYPE_DIRECT
  }

  /**
   * Returns shared label for chat contact/header/message: { text, variant } or null.
   * SA -> CheckMarq (blue), HA -> HA Admin / role name (green), Clinician -> discipline (orange).
   */
  getChatUserLabel(user) {
    if (!user?.role) return null
    const role = user.role

    if (Object.values(saUserRoleIds).includes(role)) {
      return { text: 'CheckMarq', variant: CHAT_LABEL_VARIANTS.SA }
    }
    if (Object.values(haUserRoleIds).includes(role)) {
      const name =
        role === 'ha_admin'
          ? 'HA Admin'
          : haUserRoles.find((r) => r.id === role)?.name || role
      return {
        text: name,
        variant: CHAT_LABEL_VARIANTS.HA,
      }
    }
    if (
      user.role === 'clinician' ||
      (user.discipline_value && user.discipline_value.trim())
    ) {
      const text =
        (user.discipline_value && user.discipline_value.trim()) || 'Clinician'
      return { text, variant: CHAT_LABEL_VARIANTS.CLINICIAN }
    }
    return null
  }

  prepareChatContact({ contact, currentUserUuid, listKind } = {}) {
    let prepared

    if (Array.isArray(contact.participants) && contact.participants.length) {
      const owner = contact.participants.find((p) => p.role_in_chat === 'owner')
      const participantByRole = contact.participants.find(
        (p) => p.role_in_chat === 'participant'
      )
      const peer =
        contact.participants.find((p) => p.uuid !== currentUserUuid) ??
        contact.participants[0]

      let contactUser = { ...peer }
      if (!(contactUser.full_name && contactUser.full_name.trim().length)) {
        contactUser.full_name = 'Staff Admin'
      }

      if (contact.offer?.patient_full_name) {
        contactUser.patient_full_name = contact.offer.patient_full_name
      } else if (contact.episode_of_care?.patient_full_name) {
        contactUser.patient_full_name =
          contact.episode_of_care.patient_full_name
      }

      const normalizedOwner = owner ?? contact.participants[0]
      const normalizedParticipant =
        participantByRole ??
        contact.participants.find((p) => p.uuid !== normalizedOwner?.uuid) ??
        contact.participants[1]

      prepared = {
        ...contact,
        contactUser,
        owner: normalizedOwner,
        participant: normalizedParticipant,
      }
    } else if (
      (isGroupChatPayload(contact) || listKind === CHAT_API_TYPE_GROUP) &&
      (!Array.isArray(contact.participants) ||
        contact.participants.length === 0)
    ) {
      const eoc = contact.episode_of_care
      prepared = {
        ...contact,
        contactUser: {
          full_name: eoc?.health_agency_name?.trim() || 'Group chat',
          patient_full_name: eoc?.patient_full_name?.trim() || '',
          avatar: '',
          health_agency: eoc?.health_agency_name || '',
        },
        owner: null,
        participant: null,
      }
    } else {
      let contactUser

      if (contact.owner.uuid === currentUserUuid) {
        contactUser = contact.participant
      } else {
        contactUser = contact.owner
      }

      if (!contactUser.full_name.trim().length) {
        contactUser.full_name = 'Staff Admin'
      }

      if (contact.offer?.patient_full_name) {
        contactUser.patient_full_name = contact.offer.patient_full_name
      } else if (contact.episode_of_care?.patient_full_name) {
        contactUser.patient_full_name =
          contact.episode_of_care.patient_full_name
      }

      prepared = { ...contact, contactUser }
    }

    return applyGroupChatPresentation(prepared, { listKind })
  }

  getChatWithAgencyUser({ uuid, user_id }) {
    return api.get(`${BASE}/health_agencies/${uuid}/users/${user_id}/chat/`)
  }

  getChatWithClinician({ uuid }) {
    return api.get(`${BASE}/users/clinicians/${uuid}/chat/`)
  }

  getChatWithOffer({ uuid }) {
    return api.get(`${BASE}/offers/${uuid}/chat/`)
  }

  getChatsList({ params, cancelToken }) {
    return api.get(`${BASE}/chats/`, {
      params: { ...params, limit: DEFAULT_PAGINATION_LIMIT },
      cancelToken,
    })
  }

  getAvailableForChat({ params, cancelToken }) {
    return api.get(`${BASE}/users/available_for_chat/`, { params, cancelToken })
  }

  createChat({ participant_id }) {
    return api.post(`${BASE}/chats/`, { participant_id })
  }

  getChatDetails(uuid) {
    return api.get(`${BASE}/chats/${uuid}/`)
  }

  getChatMessages({ uuid, offset = 0, limit = 50 }) {
    return api.get(`${BASE}/chats/${uuid}/messages/`, {
      params: { offset, limit },
    })
  }

  uploadMessageDocument(file) {
    const formData = new FormData()
    formData.append('file', file)

    return api.post(`${BASE}/files/message_documents/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  getMessageDocument({ uuid }) {
    return api.get(`${BASE}/files/message_documents/${uuid}/`)
  }

  deleteMessageDocument({ uuid }) {
    return api.delete(`${BASE}/files/message_documents/${uuid}/`)
  }

  sendMessage({ uuid, data }) {
    return api.post(`${BASE}/chats/${uuid}/messages/`, data)
  }

  patchChatMessage({ uuid, messageUuid, data }) {
    return api.patch(`${BASE}/chats/${uuid}/messages/${messageUuid}/`, data)
  }

  markMessagesAsRead({ uuid }) {
    return api.post(`${BASE}/chats/${uuid}/mark_as_read/`, {})
  }

  archiveChat({ uuid, isArchived }) {
    return api.patch(`${BASE}/chats/${uuid}/`, { is_archived: isArchived })
  }

  addParticipant({ chatUuid, userUuid }) {
    return api.post(`${BASE}/chats/${chatUuid}/participants/${userUuid}/`, {})
  }

  removeParticipant({ chatUuid, userUuid }) {
    return api.delete(`${BASE}/chats/${chatUuid}/participants/${userUuid}/`)
  }

  getHasUnread() {
    return api.get(`${BASE}/chats/has_unread/`)
  }
}

export default new ChatsService()
